import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Trash2, FolderOpen, Plus } from 'lucide-react';
import Navbar from '../Layout/Navbar';
import NewMapModal from './NewMapModal';
import { getMaps, getMap, deleteMap, createTile, deleteTile, getBillingStatus } from '../../api/client';

export default function Dashboard() {
  const navigate = useNavigate();
  const [maps, setMaps] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showRT, setShowRT] = useState(false);
  const [rtTiles, setRtTiles] = useState([]);
  const [rtMapId, setRtMapId] = useState(null);
  const [pendingConcepts, setPendingConcepts] = useState(null); // concepts to pre-populate after modal
  const [readOnly, setReadOnly] = useState(false); // trial expired: view and export only

  // Get active client and user info
  const userStored = localStorage.getItem('user');
  const user = userStored ? JSON.parse(userStored) : null;
  const isAdmin = user?.role === 'ADMIN';
  const stored = localStorage.getItem('activeClient');
  let activeClient = stored ? JSON.parse(stored) : null;

  // Heal sessions that logged in before activeClient was always stored
  // (otherwise CLIENT users bounce between / and /dashboard forever)
  if (!activeClient && user?.clientId) {
    activeClient = { id: user.clientId, name: user.name };
    localStorage.setItem('activeClient', JSON.stringify(activeClient));
  }

  // If no client selected, redirect to selector (admin) or login (client)
  useEffect(() => {
    if (!activeClient) {
      navigate(isAdmin ? '/clients' : '/');
    }
  }, [activeClient, navigate, isAdmin]);

  const loadData = useCallback(async () => {
    if (!activeClient) return;
    setLoading(true);
    try {
      const mapsData = await getMaps(activeClient.id);
      setMaps(mapsData);
    } catch (err) {
      console.error('Failed to load maps:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClient?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    getBillingStatus()
      .then((b) => setReadOnly(b.access === 'expired'))
      .catch(() => {});
  }, []);

  function formatDate(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  function handleSwitchClient() {
    localStorage.removeItem('activeClient');
    navigate('/clients');
  }

  async function handleDelete(e, mapId, mapName) {
    e.stopPropagation();
    if (!window.confirm(`Delete "${mapName}"? This cannot be undone.`)) return;
    try {
      await deleteMap(mapId);
      setMaps((prev) => prev.filter((m) => m.id !== mapId));
    } catch (err) {
      console.error('Failed to delete map:', err);
    }
  }

  if (!activeClient) return null;

  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      <Navbar />

      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-8">
        {/* Read-only banner for expired trials */}
        {readOnly && (
          <div className="bg-navy border border-gold/40 rounded-xl px-6 py-4 mb-8 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-gold font-heading font-bold">Your free trial has ended.</p>
              <p className="text-gray-300 text-sm mt-0.5">
                You can view and export your maps. Subscribe to keep building.
              </p>
            </div>
            <Link to="/subscribe" className="btn-gold text-sm whitespace-nowrap">
              Subscribe
            </Link>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-heading font-semibold text-txt-primary">
              {activeClient.name}
            </h1>
            {maps.length > 0 && (
              <p className="text-sm text-txt-secondary mt-1">
                Choose a Clarity Map to continue, or start a new one.
              </p>
            )}
            {isAdmin && (
              <button
                onClick={handleSwitchClient}
                className="text-xs text-txt-secondary hover:text-txt-primary mt-1"
              >
                Switch client
              </button>
            )}
          </div>
          {!readOnly && (
            <button
              onClick={() => setShowModal(true)}
              className="btn-gold flex items-center gap-2"
            >
              <span className="text-lg leading-none">+</span>
              New Clarity Map
            </button>
          )}
        </div>

        {/* Maps Grid (excludes Random Thoughts) */}
        {(() => {
          const clarityMaps = maps.filter((m) => m.name !== 'Random Thoughts');
          const rtMap = maps.find((m) => m.name === 'Random Thoughts');

          return loading ? (
            <div className="text-center text-txt-secondary py-16">Loading...</div>
          ) : clarityMaps.length === 0 && !rtMap ? (
            <div className="text-center py-16">
              <p className="text-txt-secondary mb-4">
                No clarity maps for {activeClient.name} yet.
              </p>
              {!readOnly && (
                <button onClick={() => setShowModal(true)} className="btn-gold-outline">
                  + Create First Map
                </button>
              )}
            </div>
          ) : (
            <>
              {clarityMaps.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {clarityMaps.map((map, index) => (
                    <div
                      key={map.id}
                      onClick={() => navigate(readOnly ? `/map/${map.id}/view` : `/map/${map.id}`)}
                      className={`bg-white rounded-xl shadow-md border border-gray-100 border-l-4
                        p-5 cursor-pointer hover:shadow-lg hover:border-gold/30
                        transition-all duration-150 group relative flex flex-col ${
                        index === 0
                          ? 'border-l-gold ring-2 ring-gold/30 shadow-lg'
                          : 'border-l-gold/50'
                      }`}
                    >
                      {index === 0 && (
                        <span className="absolute -top-2.5 left-4 bg-gold text-black text-[9px] font-heading font-bold uppercase tracking-widest px-2 py-0.5 rounded">
                          Last edited
                        </span>
                      )}
                      {!readOnly && (
                        <button
                          onClick={(e) => handleDelete(e, map.id, map.name)}
                          className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center
                            text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                          title="Delete map"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <h3 className="font-heading font-semibold text-txt-primary mb-3 group-hover:text-black pr-8">
                        {map.name}
                      </h3>
                      <p className="text-[10px] font-semibold text-txt-secondary uppercase tracking-wide mb-1">
                        What does success look like?
                      </p>
                      <p className="text-sm text-txt-primary font-medium mb-4 line-clamp-2">
                        {map.measurement}
                      </p>
                      <div className="mt-auto pt-2 flex items-center justify-end text-xs text-txt-secondary">
                        <span>Clarity Date: {formatDate(map.updatedAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Random Thoughts — separate small section below the grid */}
              {rtMap && (
                <div className="mt-6 border-t border-black/10 pt-4">
                  <button
                    onClick={async () => {
                      try {
                        const full = await getMap(rtMap.id);
                        setRtTiles(full.tiles || []);
                        setRtMapId(rtMap.id);
                        setShowRT(true);
                      } catch (err) { console.error(err); }
                    }}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl bg-teal/10 border border-teal/30
                      hover:bg-teal/20 transition-colors text-left w-full max-w-md"
                  >
                    <FolderOpen className="w-5 h-5 text-teal-dark shrink-0" />
                    <div>
                      <p className="text-sm font-heading font-bold uppercase text-black">Random Thoughts</p>
                      <p className="text-xs text-txt-secondary">Saved concepts from completed maps</p>
                    </div>
                  </button>
                </div>
              )}
            </>
          );
        })()}
      </main>

      {/* New Map Modal — pre-filled with active client */}
      {showModal && (
        <NewMapModal
          activeClient={activeClient}
          onClose={() => { setShowModal(false); setPendingConcepts(null); }}
          onCreated={async (newMap) => {
            // If we have pending concepts from Random Thoughts, copy them in
            if (pendingConcepts && pendingConcepts.length > 0) {
              for (const t of pendingConcepts) {
                await createTile(newMap.id, {
                  text: t.text,
                  zone: 'HOLDING_PEN',
                  sortOrder: 0,
                  posX: 240 + Math.random() * 720,
                  posY: 140 + Math.random() * 420,
                });
              }
              // Mark the Capture overlay as seen so it doesn't show
              localStorage.setItem('clarity_capture_seen', 'true');
            }
            setShowModal(false);
            setPendingConcepts(null);
            navigate(`/map/${newMap.id}`);
          }}
        />
      )}

      {/* Random Thoughts Modal */}
      {showRT && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowRT(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 max-h-[80vh] flex flex-col">
            <h2 className="text-lg font-heading font-bold uppercase tracking-wide text-black mb-1">
              Random Thoughts
            </h2>
            <p className="text-xs text-txt-secondary mb-4">Concepts saved from completed maps</p>

            {rtTiles.length === 0 ? (
              <p className="text-sm text-txt-secondary italic py-4">No saved concepts yet.</p>
            ) : (
              <div className="flex-1 overflow-auto mb-4">
                {/* Group by source map (stored in linkUrl) — each group has its own actions */}
                {Object.entries(
                  rtTiles.reduce((acc, t) => {
                    const source = t.linkUrl || 'Unknown map';
                    if (!acc[source]) acc[source] = [];
                    acc[source].push(t);
                    return acc;
                  }, {})
                ).map(([source, concepts]) => (
                  <div key={source} className="mb-4 border border-black/10 rounded-xl p-3">
                    <p className="text-[10px] font-heading font-bold uppercase tracking-widest text-txt-secondary mb-2">
                      From: {source}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {concepts.map((t) => (
                        <span key={t.id} className="bg-teal/15 border border-teal/30 text-txt-primary rounded-lg px-3 py-1.5 text-sm">
                          {t.text}
                        </span>
                      ))}
                    </div>
                    {!readOnly && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setPendingConcepts(concepts);
                          setShowRT(false);
                          setShowModal(true);
                        }}
                        className="flex items-center gap-1.5 bg-navy text-white h-8 px-3 rounded-lg font-heading font-bold uppercase text-xs hover:bg-navy-light transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" /> New Map From These
                      </button>
                      <button
                        onClick={async () => {
                          for (const t of concepts) {
                            await deleteTile(rtMapId, t.id);
                          }
                          setRtTiles((prev) => prev.filter((t) => !concepts.find((c) => c.id === t.id)));
                        }}
                        className="flex items-center gap-1 text-xs text-brand-red hover:bg-red-50 border border-brand-red/30 px-3 h-8 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => setShowRT(false)}
              className="w-full text-center text-xs text-txt-secondary hover:text-txt-primary mt-3"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
